import api, { getData } from '@/axiosConfig'
import { status, paymentMethodType, compareInvoices } from '../../helpers'

import {
  LOAD_PAYMENT_METHODS,
  SET_PAYMENT_METHODS,
  SET_UPDATED_PAYMENT_METHOD,
  REMOVE_PAYMENT_METHOD,
  ADDING_NEW_PAYMENT_METHOD,
  LOAD_INVOICES,
  SET_INVOICES,
  LOAD_PLANS,
  SET_PLANS,
  LOAD_SUBSCRIPTION_INFO,
  SET_SUBSCRIPTION_INFO,
  UPDATING_SUBSCRIPTION_INFO,
  SET_SELECTED_PLAN_IF_NO_PAYMENT_METHODS,
  LOAD_SUBSCRIPTION_ALERTS,
  SET_SUBSCRIPTION_ALERTS,
  RESET_BILLING_STATE,
  SET_PAYMENT_METHOD_COUNT,
  INVOICE_PAGINATION_COUNT
} from '@/state/mutationTypes'
import router from '@/router'
import i18n from '../../internatinalization/i18n'

const gemnoteAPI = api()

export default {
  async getPaymentMethods({ commit, dispatch }, { perPage = 50, page = 1 }) {
    try {
      commit(LOAD_PAYMENT_METHODS, status.LOADING)
      const response = await gemnoteAPI.get(
        `team/payment_methods?page[number]=${page}&page[size]=${perPage}`
      )
      const { totalCount } = response.data.meta
      const data = getData(response)
      const paymentMethods = data.map(paymentMethod => {
        return {
          id: parseInt(paymentMethod.id),
          sourceType: paymentMethod.attributes.sourceType,
          lastFour: paymentMethod.attributes.last4
            ? paymentMethod.attributes.last4
            : '',
          brand: paymentMethod.attributes.brand,
          isPrimary: paymentMethod.attributes['isDefault?'],
          stripeDefault: paymentMethod.attributes['stripeDefault?'],
          canBeRemoved: paymentMethod.attributes['canRemove?'],
          expiryDate: paymentMethod.attributes.expiryDate,
          firstName: paymentMethod.attributes.firstName,
          lastName: paymentMethod.attributes.lastName,
          email: paymentMethod.attributes.email,
          phone: paymentMethod.attributes.phone
        }
      })
      commit(SET_PAYMENT_METHOD_COUNT, totalCount)
      commit(SET_PAYMENT_METHODS, paymentMethods)
      commit(LOAD_PAYMENT_METHODS, status.LOADED)
    } catch (error) {
      console.log(error)
      commit(LOAD_PAYMENT_METHODS, status.ERROR)
      if (error.response) {
        if (error.response.status === 500 || error.response.status === 404) {
          dispatch(
            'notification/pushNotification',
            {
              type: 'error',
              messages: [
                error.response.status === 500
                  ? i18n.t('server.server-error')
                  : i18n.t('server.not-found')
              ]
            },
            { root: true }
          )
        } else {
          let { errors } = error.response.data
          dispatch(
            'notification/pushNotification',
            {
              type: 'error',
              messages: errors
            },
            { root: true }
          )
        }
      } else if (error.request) {
        dispatch(
          'notification/pushNotification',
          {
            type: 'error',
            messages: [i18n.t('server.no-response')]
          },
          { root: true }
        )
      } else {
        dispatch(
          'notification/pushNotification',
          {
            type: 'error',
            messages: [i18n.t('billing.action.get-payment-methods-error')]
          },
          { root: true }
        )
      }
    }
  },
  async addPaymentMethod(
    { commit, state, dispatch },
    { paymentType, invoiceInfo, stripeToken }
  ) {
    try {
      let response = null
      // eslint-disable-next-line no-unused-vars
      let data = null
      commit(ADDING_NEW_PAYMENT_METHOD, true)
      if (paymentType === paymentMethodType.CREDITCARD) {
        response = await gemnoteAPI.post('team/payment_methods', {
          payment_method: {
            stripe_token: stripeToken
          }
        })
        data = getData(response)
      } else {
        response = await gemnoteAPI.post('team/payment_methods', {
          payment_method: {
            first_name: invoiceInfo.firstName,
            last_name: invoiceInfo.lastName,
            email: invoiceInfo.accountEmail,
            phone: invoiceInfo.accountPhone,
            stripe_token: null
          }
        })
        data = getData(response)
      }
      commit(ADDING_NEW_PAYMENT_METHOD, false)
      dispatch(
        'notification/pushNotification',
        {
          type: 'success',
          messages: [i18n.t('billing.action.add-payment-method-success')]
        },
        { root: true }
      )

      // if user select plan and there was no payment methods created we use this flag to subscribe the user to a plan inmidiatly
      if (state.selectedPlanIfNoPaymentsCreated) {
        dispatch('subscribeToPlan', state.selectedPlanIfNoPaymentsCreated)
      } else {
        router.push({ name: 'billing', query: { tab: 'payment' } })
      }
    } catch (error) {
      console.log(error)
      commit(ADDING_NEW_PAYMENT_METHOD, false)
      if (error.response) {
        if (error.response.status === 500 || error.response.status === 404) {
          dispatch(
            'notification/pushNotification',
            {
              type: 'error',
              messages: [
                error.response.status === 500
                  ? i18n.t('server.server-error')
                  : i18n.t('server.not-found')
              ]
            },
            { root: true }
          )
        } else {
          let { errors } = error.response.data
          dispatch(
            'notification/pushNotification',
            {
              type: 'error',
              messages: errors[0].message
            },
            { root: true }
          )
        }
      } else if (error.request) {
        dispatch(
          'notification/pushNotification',
          {
            type: 'error',
            messages: [i18n.t('server.no-response')]
          },
          { root: true }
        )
      } else {
        dispatch(
          'notification/pushNotification',
          {
            type: 'error',
            messages: [i18n.t('billing.action.add-payment-method-error')]
          },
          { root: true }
        )
      }
    }
  },
  async setDefaultPaymentMethod({ commit, dispatch }, paymentMethod) {
    try {
      await gemnoteAPI.post(
        `team/payment_methods/${paymentMethod.id}/set_default`,
        paymentMethod
      )

      commit(SET_UPDATED_PAYMENT_METHOD, paymentMethod)
      dispatch(
        'notification/pushNotification',
        {
          type: 'success',
          messages: [i18n.t('billing.action.pmethod-marked-primary-success')]
        },
        { root: true }
      )
    } catch (error) {
      console.log(error)
      if (error.response) {
        if (error.response.status === 500 || error.response.status === 404) {
          dispatch(
            'notification/pushNotification',
            {
              type: 'error',
              messages: [
                error.response.status === 500
                  ? i18n.t('server.server-error')
                  : i18n.t('server.not-found')
              ]
            },
            { root: true }
          )
        } else {
          let { errors } = error.response.data
          dispatch(
            'notification/pushNotification',
            {
              type: 'error',
              messages: errors
            },
            { root: true }
          )
        }
      } else if (error.request) {
        dispatch(
          'notification/pushNotification',
          {
            type: 'error',
            messages: [i18n.t('server.no-response')]
          },
          { root: true }
        )
      } else {
        dispatch(
          'notification/pushNotification',
          {
            type: 'error',
            messages: [i18n.t('billing.action.pmethod-marked-primary-error')]
          },
          { root: true }
        )
      }
    }
  },
  async deletePaymentMethod({ commit, dispatch }, paymentMethodId) {
    try {
      await gemnoteAPI.delete(`team/payment_methods/${paymentMethodId}`)
      commit(REMOVE_PAYMENT_METHOD, paymentMethodId)
      dispatch(
        'notification/pushNotification',
        {
          type: 'success',
          messages: [i18n.t('billing.action.pmethod-removed-success')]
        },
        { root: true }
      )
    } catch (error) {
      console.log(error)
      if (error.response) {
        if (error.response.status === 500 || error.response.status === 404) {
          dispatch(
            'notification/pushNotification',
            {
              type: 'error',
              messages: [
                error.response.status === 500
                  ? i18n.t('server.server-error')
                  : i18n.t('server.not-found')
              ]
            },
            { root: true }
          )
        } else {
          let { errors } = error.response.data
          dispatch(
            'notification/pushNotification',
            {
              type: 'error',
              messages: [...errors[0].message]
            },
            { root: true }
          )
        }
      } else if (error.request) {
        dispatch(
          'notification/pushNotification',
          {
            type: 'error',
            messages: [i18n.t('server.no-response')]
          },
          { root: true }
        )
      } else {
        dispatch(
          'notification/pushNotification',
          {
            type: 'error',
            messages: [i18n.t('billing.action.pmethod-removed-error')]
          },
          { root: true }
        )
      }
    }
  },
  async getInvoices({ commit, dispatch }, { page = 1, perPage = 50 }) {
    try {
      commit(LOAD_INVOICES, status.LOADING)
      const response = await gemnoteAPI.get(
        `team/invoices?page[number]=${page}&page[size]=${perPage}`
      )
      const { totalCount } = response.data.meta
      const data = getData(response)
      const invoices = data.map(invoice => {
        return {
          id: parseInt(invoice.id),
          invoiceDate: invoice.attributes.invoiceDate,
          invoiceNumber: invoice.attributes.number,
          title: invoice.attributes.planTitle,
          formattedAmountDue: invoice.attributes.formattedAmountDue,
          status: invoice.attributes.status,
          extenalStripeURL: invoice.attributes.hostedInvoiceUrl
        }
      })
      commit(INVOICE_PAGINATION_COUNT, totalCount)
      commit(LOAD_INVOICES, status.LOADED)
      commit(SET_INVOICES, invoices.sort(compareInvoices))
    } catch (error) {
      console.log(error)
      commit(LOAD_INVOICES, status.ERROR)
      if (error.response) {
        if (error.response.status === 500 || error.response.status === 404) {
          dispatch(
            'notification/pushNotification',
            {
              type: 'error',
              messages: [
                error.response.status === 500
                  ? i18n.t('server.server-error')
                  : i18n.t('server.not-found')
              ]
            },
            { root: true }
          )
        } else {
          let { errors } = error.response.data
          dispatch(
            'notification/pushNotification',
            {
              type: 'error',
              messages: errors
            },
            { root: true }
          )
        }
      } else if (error.request) {
        dispatch(
          'notification/pushNotification',
          {
            type: 'error',
            messages: [i18n.t('server.no-response')]
          },
          { root: true }
        )
      } else {
        dispatch(
          'notification/pushNotification',
          {
            type: 'error',
            messages: [i18n.t('billing.action.get-invoices-error')]
          },
          { root: true }
        )
      }
    }
  },
  async getPlans({ commit, dispatch }) {
    try {
      commit(LOAD_PLANS, status.LOADING)
      const response = await gemnoteAPI.get('team/plans')
      const data = getData(response)

      const paymentPlans = data.map(plan => {
        return {
          id: parseInt(plan.id),
          name: plan.attributes.name,
          currency: plan.attributes.currency,
          interval: plan.attributes.interval,
          intervalCount: plan.attributes.intervalCount,
          dollarAmount: parseFloat(plan.attributes.amount).toFixed(0),
          internationalShipment: plan.attributes['internationalShipment?'],
          amountInCents: plan.attributes.amountInCents,
          trialPeriodDays: plan.attributes.trialPeriodDays,
          ordersLimit: plan.attributes.ordersLimit,
          usersLimit: plan.attributes.usersLimit,
          whareHouseLimit: plan.attributes.itemsLimit
        }
      })
      commit(SET_PLANS, paymentPlans)
      commit(LOAD_PLANS, status.LOADED)
      await dispatch('getCurrentSubscription')
    } catch (error) {
      console.log(error)
      commit(LOAD_PLANS, status.ERROR)
      if (error.response) {
        if (error.response.status === 500 || error.response.status === 404) {
          dispatch(
            'notification/pushNotification',
            {
              type: 'error',
              messages: [
                error.response.status === 500
                  ? i18n.t('server.server-error')
                  : i18n.t('server.not-found')
              ]
            },
            { root: true }
          )
        } else {
          let { errors } = error.response.data
          dispatch(
            'notification/pushNotification',
            {
              type: 'error',
              messages: errors
            },
            { root: true }
          )
        }
      } else if (error.request) {
        dispatch(
          'notification/pushNotification',
          {
            type: 'error',
            messages: [i18n.t('server.no-response')]
          },
          { root: true }
        )
      } else {
        dispatch(
          'notification/pushNotification',
          {
            type: 'error',
            messages: [i18n.t('billing.action.get-plans-error')]
          },
          { root: true }
        )
      }
    }
  },
  validatesSubscriptionRequirements({ commit, state, dispatch }, planId) {
    return new Promise((resolve, reject) => {
      // check if there is at least one payment method
      let paymentMethods = state.paymentMethods
      if (paymentMethods.length) {
        resolve()
      } else {
        dispatch(
          'notification/pushNotification',
          {
            type: 'error',
            messages: [i18n.t('billing.action.add-pmethod-first')]
          },
          { root: true }
        )
        commit(SET_SELECTED_PLAN_IF_NO_PAYMENT_METHODS, planId)
        reject({ success: false, message: 'no_payment_method' })
      }
    })
  },
  async subscribeToPlan({ commit, state, dispatch }, planId) {
    try {
      let body = {
        paige_subscription: {
          plan_id: planId
        }
      }

      const response = await gemnoteAPI.post('team/subscription', body)
      const data = getData(response)
      if (data) {
        let currentPlan = {
          id: data.attributes.planId,
          index: state.plans.findIndex(
            plan => plan.id === data.attributes.planId
          )
        }
        commit(SET_SUBSCRIPTION_INFO, currentPlan)
        commit(SET_SELECTED_PLAN_IF_NO_PAYMENT_METHODS, null)

        dispatch(
          'notification/pushNotification',
          {
            type: 'success',
            messages: [i18n.t('billing.action.subscription-success')]
          },
          { root: true }
        )
      } else {
        commit(SET_SUBSCRIPTION_INFO, null)
        dispatch(
          'notification/pushNotification',
          {
            type: 'error',
            messages: [i18n.t('billing.action.subscription-error')]
          },
          { root: true }
        )
      }
      let currentPage = window.location.pathname
      if (currentPage !== '/manage-billing') router.push({ name: 'billing' })
    } catch (error) {
      console.log(error)
      if (error.response) {
        if (error.response.status === 500 || error.response.status === 404) {
          dispatch(
            'notification/pushNotification',
            {
              type: 'error',
              messages: [
                error.response.status === 500
                  ? i18n.t('server.server-error')
                  : i18n.t('server.not-found')
              ]
            },
            { root: true }
          )
        } else {
          let { errors } = error.response.data
          dispatch(
            'notification/pushNotification',
            {
              type: 'error',
              messages: errors[0].message
            },
            { root: true }
          )
        }
      } else if (error.request) {
        dispatch(
          'notification/pushNotification',
          {
            type: 'error',
            messages: [i18n.t('server.no-response')]
          },
          { root: true }
        )
      } else {
        dispatch(
          'notification/pushNotification',
          {
            type: 'error',
            messages: [i18n.t('billing.action.subsciption-exception')]
          },
          { root: true }
        )
      }
    }
  },
  async updatePlan({ commit, state, dispatch }, planId) {
    commit(UPDATING_SUBSCRIPTION_INFO, true)
    try {
      let body = {
        paige_subscription: {
          plan_id: planId
        }
      }

      const response = await gemnoteAPI.put('team/subscription', body)
      const data = getData(response)
      if (data) {
        let currentPlan = {
          id: data.attributes.planId,
          index: state.plans.findIndex(
            plan => plan.id === data.attributes.planId
          )
        }
        commit(SET_SUBSCRIPTION_INFO, currentPlan)
        commit(SET_SELECTED_PLAN_IF_NO_PAYMENT_METHODS, null)

        dispatch(
          'notification/pushNotification',
          {
            type: 'success',
            messages: [i18n.t('billing.action.subscription-update-success')]
          },
          { root: true }
        )
      } else {
        commit(SET_SUBSCRIPTION_INFO, null)
        dispatch(
          'notification/pushNotification',
          {
            type: 'error',
            messages: [i18n.t('billing.action.subscription-update-error')]
          },
          { root: true }
        )
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status === 500 || error.response.status === 404) {
          dispatch(
            'notification/pushNotification',
            {
              type: 'error',
              messages: [
                error.response.status === 500
                  ? i18n.t('server.server-error')
                  : i18n.t('server.not-found')
              ]
            },
            { root: true }
          )
        } else {
          let { errors } = error.response.data
          dispatch(
            'notification/pushNotification',
            {
              type: 'error',
              messages: errors[0].message
            },
            { root: true }
          )
        }
      } else if (error.request) {
        dispatch(
          'notification/pushNotification',
          {
            type: 'error',
            messages: [i18n.t('server.no-response')]
          },
          { root: true }
        )
      } else {
        dispatch(
          'notification/pushNotification',
          {
            type: 'error',
            messages: [i18n.t('billing.action.subscription-update-exception')]
          },
          { root: true }
        )
      }
    } finally {
      commit(UPDATING_SUBSCRIPTION_INFO, false)
    }
  },
  async getCurrentSubscription({ commit, state, dispatch }) {
    try {
      commit(LOAD_SUBSCRIPTION_INFO, status.LOADING)
      const response = await gemnoteAPI.get('team/subscription')
      const data = getData(response)
      if (data) {
        let currentPlan = {
          id: data.attributes.planId,
          index: state.plans.findIndex(
            plan => plan.id === data.attributes.planId
          ),
          ...data.attributes
        }
        commit(SET_SUBSCRIPTION_INFO, currentPlan)
        commit(LOAD_SUBSCRIPTION_INFO, status.LOADED)
        return
      }
      commit(SET_SUBSCRIPTION_INFO, {})
      commit(LOAD_SUBSCRIPTION_INFO, status.LOADED)
    } catch (error) {
      console.log(error)
      commit(SET_SUBSCRIPTION_INFO, {})
      commit(LOAD_SUBSCRIPTION_INFO, status.ERROR)
      if (error.response) {
        if (error.response.status === 500 || error.response.status === 404) {
          dispatch(
            'notification/pushNotification',
            {
              type: 'error',
              messages: [
                error.response.status === 500
                  ? i18n.t('server.server-error')
                  : i18n.t('server.not-found')
              ]
            },
            { root: true }
          )
        } else {
          let { errors } = error.response.data
          dispatch(
            'notification/pushNotification',
            {
              type: 'error',
              messages: errors
            },
            { root: true }
          )
        }
      } else if (error.request) {
        dispatch(
          'notification/pushNotification',
          {
            type: 'error',
            messages: [i18n.t('server.no-response')]
          },
          { root: true }
        )
      } else {
        dispatch(
          'notification/pushNotification',
          {
            type: 'error',
            messages: [i18n.t('billing.action.get-currentplan-error')]
          },
          { root: true }
        )
      }
    }
  },
  async checkForTeamAlerts({ commit, dispatch }) {
    try {
      commit(LOAD_SUBSCRIPTION_ALERTS, status.LOADING)
      const response = await gemnoteAPI.get('team/alerts')
      const data = getData(response)

      let teamAlerts = []
      const alert_messages = data[0].alert_messages

      if (alert_messages.length) {
        teamAlerts = alert_messages.map(alert => {
          return {
            message: alert
          }
        })
      } else {
        teamAlerts = []
      }

      commit(SET_SUBSCRIPTION_ALERTS, teamAlerts)
      commit(LOAD_SUBSCRIPTION_ALERTS, status.LOADED)
    } catch (error) {
      console.log(error)
      commit(LOAD_SUBSCRIPTION_ALERTS, status.ERROR)
      if (error.response) {
        if (error.response.status === 500 || error.response.status === 404) {
          dispatch(
            'notification/pushNotification',
            {
              type: 'error',
              messages: [
                error.response.status === 500
                  ? i18n.t('server.server-error')
                  : i18n.t('server.not-found')
              ]
            },
            { root: true }
          )
        } else {
          let { errors } = error.response.data
          dispatch(
            'notification/pushNotification',
            {
              type: 'error',
              messages: errors
            },
            { root: true }
          )
        }
      } else if (error.request) {
        dispatch(
          'notification/pushNotification',
          {
            type: 'error',
            messages: [i18n.t('server.no-response')]
          },
          { root: true }
        )
      } else {
        dispatch(
          'notification/pushNotification',
          {
            type: 'error',
            messages: [i18n.t('billing.action.get-alerts-error')]
          },
          { root: true }
        )
      }
    }
  },
  resetBillingState({ commit }) {
    commit(RESET_BILLING_STATE)
  }
}
